import { AdminApiError, adminClient } from '@/api/clients/admin-client'
import { compactSystemQuery } from '@/api/admin/system'
import type { AdminPageData, AdminPageQuery } from '@/shared/types/admin'
import type { ApiResponse } from '@/shared/types/http'
import type { SystemRecord } from '@/api/admin/system'

export type AdminNoticeId = string | number

export interface AdminNotice {
  id: AdminNoticeId
  noticeType: number
  title: string
  content: string
  receiverType?: number
  receiverTypeIds?: string
  receiverTypeList?: AdminNoticeId[]
  status?: number
  senderName?: string
  senderDate?: string | null
  createDate?: string | null
  readStatus?: number
  readDate?: string | null
}

export interface AdminNoticeRecipient {
  receiverName: string
  readStatus: number
  readDate?: string | null
}

export interface AdminNoticeFilters {
  noticeType: string | number
  title?: string
  status?: string | number
}

export interface AdminNoticePayload {
  id?: AdminNoticeId
  noticeType: number
  title: string
  content: string
  receiverType: number
  receiverTypeList: AdminNoticeId[]
  status: number
}

const noticePath = '/sys/notice'

const requireId = (id: AdminNoticeId): string => {
  const value = String(id).trim()
  if (!value || (typeof id === 'number' && !Number.isFinite(id))) throw new Error('通知 ID 不合法')
  return encodeURIComponent(value)
}

const parsePage = <T>(data: unknown, code: string | number, isItem: (item: unknown) => item is T, label: string): AdminPageData<T> => {
  const value = data as Partial<AdminPageData<unknown>> | undefined
  if (!value || !Array.isArray(value.list) || typeof value.total !== 'number' || !value.list.every(isItem)) {
    throw new AdminApiError(`${label}分页响应格式错误`, code)
  }
  return value as AdminPageData<T>
}

const isNotice = (value: unknown): value is AdminNotice => {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<AdminNotice>
  return (typeof item.id === 'string' || typeof item.id === 'number') &&
    typeof item.noticeType === 'number' && typeof item.title === 'string' && typeof item.content === 'string'
}

const isRecipient = (value: unknown): value is AdminNoticeRecipient => {
  if (!value || typeof value !== 'object') return false
  const item = value as Partial<AdminNoticeRecipient>
  return typeof item.receiverName === 'string' && typeof item.readStatus === 'number'
}

export const adminNoticeApi = {
  async page(query: AdminPageQuery<AdminNoticeFilters>): Promise<AdminPageData<AdminNotice>> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/page`, { params: compactSystemQuery(query) })
    return parsePage(response.data.data, response.data.code, isNotice, '通知')
  },
  async myPage(query: AdminPageQuery<Pick<AdminNoticeFilters, 'noticeType'>>): Promise<AdminPageData<AdminNotice>> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/mynotice/page`, { params: compactSystemQuery(query) })
    return parsePage(response.data.data, response.data.code, isNotice, '我的通知')
  },
  async recipientPage(noticeId: AdminNoticeId, page: number, limit: number): Promise<AdminPageData<AdminNoticeRecipient>> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/user/page`, { params: { noticeId: requireId(noticeId), page, limit } })
    return parsePage(response.data.data, response.data.code, isRecipient, '接收记录')
  },
  async detail(id: AdminNoticeId): Promise<AdminNotice> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/${requireId(id)}`)
    if (!isNotice(response.data.data)) throw new AdminApiError('通知详情响应格式错误', response.data.code)
    return response.data.data
  },
  async departments(): Promise<SystemRecord[]> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/departments`)
    if (!Array.isArray(response.data.data)) throw new AdminApiError('通知部门选项响应格式错误', response.data.code)
    return response.data.data as SystemRecord[]
  },
  async create(payload: AdminNoticePayload): Promise<void> { await adminClient.post<ApiResponse>(noticePath, payload) },
  async update(payload: AdminNoticePayload): Promise<void> { await adminClient.put<ApiResponse>(noticePath, payload) },
  async remove(ids: readonly AdminNoticeId[]): Promise<void> {
    const targets = [...new Set(ids)].filter(id => String(id).trim())
    if (!targets.length) throw new Error('通知删除目标不能为空')
    await adminClient.delete<ApiResponse>(noticePath, { data: targets })
  },
  async unread(): Promise<number> {
    const response = await adminClient.get<ApiResponse<unknown>>(`${noticePath}/mynotice/unread`)
    if (typeof response.data.data !== 'number') throw new AdminApiError('未读数响应格式错误', response.data.code)
    return response.data.data
  },
  async markRead(id: AdminNoticeId): Promise<void> {
    await adminClient.put<ApiResponse>(`${noticePath}/mynotice/read/${requireId(id)}`)
  }
}

export const ADMIN_NOTICE_READ_EVENT = 'kesai:admin-notice-read'
